// Componente de reporte de pedidos enviados por cliente hijo (tenant_3pl)
// Child team orders report component (tenant_3pl)
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Input } from '@shared/components/ui/shadcn/input';
import { ArrowDown, ArrowUp, ArrowUpDown, Package, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChildTeamOrders } from '../../hooks/useChildTeamOrders';

type SortField = 'name' | 'orders';
type SortDirection = 'asc' | 'desc' | null;

export const ChildTeamOrdersReport = () => {
  const { t } = useTranslation('homeModule');
  const { childTeamOrders, isLoading, error } = useChildTeamOrders();

  // 🎯 Estados para búsqueda y ordenamiento (por defecto ordenado por pedidos desc)
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField | null>('orders');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // 🎯 Función para manejar el ordenamiento
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Si ya está ordenado por este campo, cambiar dirección
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        // Si ya está desc, quitar el ordenamiento
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      // Nuevo campo, empezar con ascendente
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // 🎯 Calcular total de pedidos ORIGINAL (sin filtrar) para los porcentajes
  const totalOrders = useMemo(() => {
    return childTeamOrders.child_teams.reduce((sum, team) => sum + team.orders_sent_today, 0);
  }, [childTeamOrders.child_teams]);

  // 🎯 Filtrar y ordenar los datos
  const filteredAndSortedTeams = useMemo(() => {
    let teams = [...childTeamOrders.child_teams];

    // Aplicar búsqueda
    if (searchTerm.trim()) {
      teams = teams.filter((team) => team.team_name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    // Aplicar ordenamiento
    if (sortField && sortDirection) {
      teams.sort((a, b) => {
        if (sortField === 'name') {
          const comparison = a.team_name.localeCompare(b.team_name);
          return sortDirection === 'asc' ? comparison : -comparison;
        } else {
          // sortField === 'orders'
          const comparison = a.orders_sent_today - b.orders_sent_today;
          return sortDirection === 'asc' ? comparison : -comparison;
        }
      });
    }

    return teams;
  }, [childTeamOrders.child_teams, searchTerm, sortField, sortDirection]);

  if (isLoading) {
    return (
      <SectionBox className="bg-card-bg" data-testid="child-team-orders-loading">
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-complementary-text">{t('child_team_orders.loading')}</p>
          </div>
        </div>
      </SectionBox>
    );
  }

  if (error) {
    return (
      <SectionBox className="bg-card-bg border-destructive/50" data-testid="child-team-orders-error">
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-destructive">{t('child_team_orders.error')}</p>
        </div>
      </SectionBox>
    );
  }

  // 🎯 Función para obtener el icono de ordenamiento
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-complementary-text" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="w-3.5 h-3.5 text-foreground" />;
    }
    return <ArrowDown className="w-3.5 h-3.5 text-foreground" />;
  };

  return (
    <SectionBox className="bg-card-bg" data-testid="child-team-orders-report">
      {/* Header con título y total */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Package className="w-4 h-4 text-foreground" />
          {t('child_team_orders.title')}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-complementary-text">Total:</span>
          <span className="text-sm font-bold bg-muted text-foreground px-3 py-1 rounded-full">
            {totalOrders} {t('common.orders')}
          </span>
        </div>
      </div>

      {/* Buscador */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-complementary-text" />
          <Input
            type="text"
            placeholder={t('child_team_orders.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 bg-background border-card-border focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
      </div>

      {/* Headers con ordenamiento */}
      <div className="flex items-center justify-between mb-3 px-1">
        <button
          onClick={() => handleSort('name')}
          className="flex items-center gap-1.5 text-xs font-medium text-complementary-text hover:text-foreground transition-colors"
        >
          {t('child_team_orders.table.client')}
          <span className="w-3.5">{getSortIcon('name')}</span>
        </button>
        <button
          onClick={() => handleSort('orders')}
          className="flex items-center gap-1.5 text-xs font-medium text-complementary-text hover:text-foreground transition-colors"
        >
          {t('child_team_orders.table.orders_sent')}
          <span className="w-3.5">{getSortIcon('orders')}</span>
        </button>
      </div>

      {/* Lista de clientes con barras de progreso */}
      {filteredAndSortedTeams.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-complementary-text">
          <p className="text-sm">
            {searchTerm.trim() ? t('child_team_orders.no_results') : t('child_team_orders.empty')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAndSortedTeams.map((team) => {
            const percentage = totalOrders > 0 ? (team.orders_sent_today / totalOrders) * 100 : 0;

            return (
              <div key={team.team_id} className="space-y-2" data-testid={`team-row-${team.team_id}`}>
                {/* Nombre del cliente y cantidad */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{team.team_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-foreground">{team.orders_sent_today}</span>
                    <span className="text-xs text-complementary-text">({percentage.toFixed(1)}%)</span>
                  </div>
                </div>

                {/* Barra de progreso */}
                <div className="w-full bg-card-border rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionBox>
  );
};
